/**
 * Utilitário para criar metadata de erro padronizada para mensagens
 * 
 * Garante que todas as mensagens com status 'failed' tenham
 * informações de erro no metadata para exibição no modal de detalhes.
 * 
 * @version 1.0.0
 */

import type { Json } from '@/integrations/supabase/types';

/**
 * Estrutura padronizada de erro para mensagens
 * Compatível com ErrorDetailsDialog e MessageStatusIndicator
 * Usa Record para compatibilidade com tipo Json do Supabase
 */
export type ErrorMetadata = {
  error: {
    /** Código do erro (ex: '500', 'PHONE_NOT_ON_WHATSAPP', 'TIMEOUT') */
    code: string;
    /** Título resumido do erro */
    title: string;
    /** Detalhes adicionais do erro */
    details: string;
    /** Mensagem técnica (resposta do provider em JSON) */
    message: string;
    /** Timestamp ISO de quando o erro ocorreu */
    occurred_at: string;
    [key: string]: string; // Index signature para compatibilidade com Json
  };
  [key: string]: unknown; // Index signature para compatibilidade com Json
};

/**
 * Cria objeto metadata de erro padronizado para mensagens com status 'failed'
 * 
 * Usado em todos os pontos do código que marcam mensagens como falhas
 * para garantir que o modal de detalhes do erro funcione corretamente.
 * 
 * @param error - Erro capturado (Error, objeto ou unknown)
 * @param sendResult - Resultado da edge function send-message (opcional)
 * @returns Objeto metadata com estrutura de erro padronizada
 * 
 * @example
 * // Uso básico com erro
 * await supabase.from('messages').update({ 
 *   status: 'failed',
 *   metadata: createErrorMetadata(error)
 * }).eq('id', messageId);
 * 
 * @example
 * // Uso com resultado da edge function
 * if (!sendResult?.success) {
 *   await supabase.from('messages').update({ 
 *     status: 'failed',
 *     metadata: createErrorMetadata(sendError, sendResult)
 *   }).eq('id', messageId);
 * }
 */
/**
 * Extrai código numérico de erro embutido em texto de mensagens de provider
 * Procura padrões como [132001], (#132001), ou código no início da string
 * 
 * @param text - Texto contendo possível código de erro embutido
 * @returns Código extraído ou null se não encontrar
 * 
 * @example
 * extractEmbeddedErrorCode("[132001] (#132001) Template not found") // "132001"
 * extractEmbeddedErrorCode("Erro genérico sem código") // null
 */
export const extractEmbeddedErrorCode = (text: string | undefined | null): string | null => {
  if (!text) return null;
  
  // Padrão 1: [XXXXX] — ex: [132001]
  const bracketMatch = text.match(/\[(\d{4,6})\]/);
  if (bracketMatch) return bracketMatch[1];
  
  // Padrão 2: (#XXXXX) — ex: (#132001)
  const hashMatch = text.match(/\(#(\d{4,6})\)/);
  if (hashMatch) return hashMatch[1];
  
  // Padrão 3: código no início da string — ex: "132001 Template not found"
  const startMatch = text.match(/^(\d{4,6})\s/);
  if (startMatch) return startMatch[1];
  
  return null;
};

export const createErrorMetadata = (
  error: unknown, 
  sendResult?: { 
    error_code?: string;
    code?: string;
    error?: string; 
    details?: string; 
    provider_response?: unknown;
    success?: boolean;
  } | null
): Json => {
  // Extração inteligente do código real do erro
  // Prioridade: error_code explícito > code > regex no texto > fallback 500
  const resolvedCode = 
    sendResult?.error_code || 
    sendResult?.code ||
    extractEmbeddedErrorCode(sendResult?.error) ||
    extractEmbeddedErrorCode(sendResult?.details) ||
    (error as { code?: string })?.code || 
    '500';

  return {
    error: {
      code: resolvedCode,
      title: sendResult?.error || (error as Error)?.message || 'Erro ao enviar mensagem',
      details: sendResult?.details || sendResult?.error || (error as Error)?.message || 'Detalhes não disponíveis',
      message: JSON.stringify(sendResult?.provider_response || error || {}),
      occurred_at: new Date().toISOString(),
    }
  };
};

/**
 * Cria metadata de erro genérica para mensagens existentes sem detalhes
 * Usado como fallback quando metadata está vazio mas status é 'failed'
 * 
 * @returns Objeto metadata com mensagem genérica
 */
export const createGenericErrorMetadata = (): Json => ({
  error: {
    code: 'UNKNOWN',
    title: 'Erro no envio',
    details: 'Detalhes do erro não disponíveis. O erro ocorreu antes da implementação do registro detalhado.',
    message: '{}',
    occurred_at: new Date().toISOString(),
  }
});
